import {
  DirectionalLight,
  EquirectangularReflectionMapping,
  LinearSRGBColorSpace,
  Scene,
  TextureLoader,
  Vector3,
} from "three";
import type { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { EditorTransformController } from "../../../editor/EditorTransformController";
import { EngineCamera } from "../../../engine/camera/Camera";
import type { Engine } from "../../../engine/Engine";
import { isConfiguratorRequiredJointType } from "../../../util/JointTypes";
import { logger } from "../../../util/Logger";
import { wait } from "../../../util/Wait";
import type { MODEL_PART_TYPE } from "../../../core/configurator/ConfiguratorData";
import { getConfiguratorData } from "../../../core/configurator/ConfiguratorData";
import { emptyConfiguratorRuntimeDocument } from "../ConfiguratorRuntimeDocument";
import { setConfiguratorPendingDragModel } from "../ConfiguratorPendingDrag";
import {
  ConfiguratorDragPreviewController,
  type ConfiguratorDragPreviewPort,
} from "../ConfiguratorDragPreviewController";
import { ConfiguratorController } from "../SugarConfiguratorController";
import type { ConfiguratorDragPreviewState } from "../ConfiguratorMessageContracts";
import type { SugarModel } from "../../../loader/SugarModel";

export type ConfiguratorMode = "editor" | "configurator" | "both";

export class ConfiguratorSessionController {
  engine?: Engine;
  configuratorController?: ConfiguratorController;
  editorController?: EditorTransformController;
  /** Owns palette / canvas drag preview. */
  dragPreview?: ConfiguratorDragPreviewPort;
  private dragPreviewController?: ConfiguratorDragPreviewController;
  private readonly started: Promise<void>;
  private resolveStarted!: () => void;
  private orbitControls?: OrbitControls;
  private configuratorId = "default";
  private currentMode: ConfiguratorMode = "configurator";

  constructor(
    private readonly onDragPreviewState: (
      state: ConfiguratorDragPreviewState,
    ) => void = () => {},
  ) {
    this.started = new Promise<void>((resolve) => {
      this.resolveStarted = resolve;
    });
  }

  start(
    engine: Engine,
    type: "editor" | "configurator" = "editor",
  ): ConfiguratorController {
    if (this.engine === engine && this.configuratorController) {
      this.mode = type;
      return this.configuratorController;
    }
    this.engine = engine;
    const activeCamera = new EngineCamera();
    this.orbitControls = activeCamera.controller as OrbitControls;
    activeCamera.instance.userData.controller = this.orbitControls;
    activeCamera.instance.position.set(5, 5, 5);
    activeCamera.instance.lookAt(new Vector3(0, 0, 0));
    engine.activeCamera = activeCamera;

    engine.activeScene?.add(new DirectionalLight());

    this.dragPreviewController = new ConfiguratorDragPreviewController(
      () => this.engine,
      () => getConfiguratorData(this.configuratorId),
      this.onDragPreviewState,
    );
    this.dragPreview = this.dragPreviewController;

    this.configuratorController = new ConfiguratorController(
      this.orbitControls,
      this.configuratorId,
      this.dragPreview,
    );
    this.resolveStarted();
    engine.activeScene?.add(this.configuratorController);
    engine.addEntity(this.configuratorController);

    this.editorController = new EditorTransformController(this.orbitControls);
    engine.addEntity(this.editorController);
    this.mode = type;
    this.loadEnvironment();
    this.dispatchSceneChanged();
    return this.configuratorController;
  }

  set id(value: string) {
    this.configuratorId = value;
  }

  get id(): string {
    return this.configuratorId;
  }

  set mode(value: ConfiguratorMode) {
    this.currentMode = value;
    if (this.configuratorController) {
      this.configuratorController.enabled =
        value === "configurator" || value === "both";
    }
    if (this.editorController) {
      this.editorController.enabled = value === "editor" || value === "both";
    }
  }

  get mode(): ConfiguratorMode {
    return this.currentMode;
  }

  beginPaletteDrag(event: DragEvent, model?: unknown): SugarModel | undefined {
    if (model) setConfiguratorPendingDragModel(model);
    if (!this.dragPreviewController) {
      logger.warn("Configurator palette drag before session start");
      return undefined;
    }
    return this.dragPreviewController.begin(event);
  }

  cancelPaletteDrag(): void {
    const cancelled = this.dragPreviewController?.cancelUncommitted();
    this.configuratorController?.releaseCancelledPreview(cancelled);
  }

  dispose(): void {
    this.dragPreviewController?.cancelAll();
    if (this.configuratorController) {
      this.engine?.removeEntity(this.configuratorController);
      this.configuratorController.removeFromParent();
    }
    if (this.editorController) {
      this.engine?.removeEntity(this.editorController);
    }
    this.configuratorController = undefined;
    this.editorController = undefined;
    this.dragPreviewController = undefined;
    this.dragPreview = undefined;
    this.engine = undefined;
  }

  async changeMaterial(materialConfig: any[]): Promise<void> {
    const controller = await this.controllerWhenStarted();
    if (controller.runtimeLoading) return;
    const map = new Map<string, { code: string; material: any }>();
    (materialConfig ?? []).forEach((material) =>
      map.set(material.code, material),
    );
    controller.setMaterial(map);
    await controller.applyMaterial();
    controller.snapshot("Change material");
    this.dispatchSceneChanged();
  }

  async changePart(partConfig: MODEL_PART_TYPE): Promise<void> {
    const controller = await this.controllerWhenStarted();
    if (controller.runtimeLoading) return;
    controller.setMainPart(partConfig);
    await controller.changeMainPart();
    await controller.applyMaterial();
    controller.snapshot("Change part");
    this.dispatchSceneChanged();
  }

  async changeSidePart(sideConfig: MODEL_PART_TYPE): Promise<void> {
    const controller = await this.controllerWhenStarted();
    if (controller.runtimeLoading) return;
    controller.setSidePart(sideConfig);
    await controller.changeSidePart();
    await controller.applyMaterial();
    controller.snapshot("Change side");
    this.dispatchSceneChanged();
  }

  async clearAll(): Promise<void> {
    const controller = await this.controllerWhenStarted();
    await controller.loadRuntimeDocument(
      emptyConfiguratorRuntimeDocument(this.configuratorId),
    );
    this.engine?.dispatchCustomEvent("model-deselected");
    controller.snapshot("Reset scene");
    this.dispatchSceneChanged();
  }

  async setTopViewForRender(): Promise<void> {
    await wait(100);
    this.engine?.dispatchCustomEvent("showTopView", { detail: true });
    this.engine?.dispatchCustomEvent("showDimensions", { detail: true });
  }

  async takeScreenshot(): Promise<string | null> {
    const blob = await this.engine?.renderer.screenshot("image/png", 1);
    return blob ? URL.createObjectURL(blob) : null;
  }

  async takeThumbnnail(): Promise<string | null> {
    const scene = new Scene();
    void scene;
    return null;
  }

  showTopView(): void {
    this.engine?.dispatchCustomEvent("showTopView", { detail: false });
  }

  showDimensions(): void {
    this.engine?.dispatchCustomEvent("showDimensions");
  }

  get products() {
    return this.configuratorController?.configurators.flatMap(
      (model) => model.products,
    );
  }

  validateConfigurations(): boolean {
    let valid = true;
    this.products?.forEach((product) => {
      product.forEach((part) => {
        if (part?.userData.info.id !== 60344 && part?.userData.info.id !== 87826) {
          return;
        }
        part.joints?.forEach((joint) => {
          if (isConfiguratorRequiredJointType(joint.inType) && !joint.binded) {
            valid = false;
            logger.info("validation false");
          }
        });
      });
    });
    return valid;
  }

  private dispatchSceneChanged(): void {
    const scene = this.engine?.activeScene;
    if (scene) this.engine?.dispatchCustomEvent("scene-changed", { detail: scene });
  }

  private async controllerWhenStarted(): Promise<ConfiguratorController> {
    await this.started;
    return this.configuratorController!;
  }

  private loadEnvironment(): void {
    void new TextureLoader()
      .loadAsync(
        "https://img-sugartech.mncdn.com/cdn.sugartech/mottobucket/FFC6JX4TV8.png",
      )
      .then((texture) => {
        if (!this.engine?.activeScene) return;
        texture.mapping = EquirectangularReflectionMapping;
        texture.colorSpace = LinearSRGBColorSpace;
        this.engine.activeScene.environment = texture;
      });
  }
}
